import React, { useRef, useState, useEffect } from "react";
import { api, formatApiError, getStoredToken } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = ".jpg,.jpeg,.png,.webp";
const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Compact image uploader/preview for a single training task.
 *
 * The image is stored as an attachment with scope='training_task' and
 * scope_id = `${trainingId}::${taskKey}` where taskKey is "warmup",
 * 1/2/3 (or "main_1"...) or "cooldown". The attachment id is also saved on
 * the training document (e.g. `main_1_image`) so subsequent loads can show it.
 */
export default function TaskImageUpload({
  trainingId, taskN, taskKey, imageId, canEdit, onChange,
}) {
  // Backwards compat: `taskN` (number) is still supported.
  const key = taskKey ?? `main_${taskN}`;
  const labelN = taskN ?? key;
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  // Resolve image preview whenever the id changes
  useEffect(() => {
    if (!imageId) {
      setMeta(null);
      setPreviewUrl(null);
      return;
    }
    setLoading(true);
    let revoke;
    const token = getStoredToken();
    fetch(`${BACKEND}/api/attachments/${imageId}/download`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.blob();
      })
      .then((b) => {
        const u = window.URL.createObjectURL(b);
        revoke = u;
        setPreviewUrl(u);
        setMeta({ ok: true });
      })
      .catch(() => {
        setPreviewUrl(null);
        setMeta(null);
      })
      .finally(() => setLoading(false));
    return () => { if (revoke) window.URL.revokeObjectURL(revoke); };
  }, [imageId]);

  const onPick = () => inputRef.current?.click();

  const onChangeFile = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = "";
    if (!rawFile) return;
    const ext = (rawFile.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.has(ext)) {
      toast.error("Solo JPG, PNG o WebP");
      return;
    }
    if (rawFile.size > MAX_BYTES) {
      toast.error("Imagen demasiado grande (máx 10 MB)");
      return;
    }
    setUploading(true);
    const file = await compressImage(rawFile);
    const fd = new FormData();
    fd.append("scope", "training_task");
    fd.append("scope_id", `${trainingId}::${key}`);
    fd.append("file", file);
    try {
      const r = await api.post("/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // delete the previous one (soft delete) so we never accumulate orphans
      if (imageId && imageId !== r.data.id) {
        try {
          await api.delete(`/attachments/${imageId}`);
        } catch (delErr) {
          // Cleanup is best-effort: the new image is already saved
          console.warn("No se pudo eliminar la imagen antigua", delErr);
        }
      }
      onChange?.(r.data.id);
      toast.success("Imagen guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!imageId) return;
    if (!window.confirm("¿Quitar la imagen de esta tarea?")) return;
    try {
      await api.delete(`/attachments/${imageId}`);
      onChange?.("");
      toast.success("Imagen quitada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando imagen...
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {previewUrl ? (
        <div className="relative inline-block">
          <img
            src={previewUrl}
            alt={`Tarea ${labelN}`}
            className="max-h-48 rounded-md border border-slate-200"
            data-testid={`task-image-${labelN}`}
          />
          {canEdit && (
            <button
              type="button"
              onClick={remove}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-200 grid place-items-center hover:bg-red-50"
              title="Quitar imagen"
              data-testid={`task-image-remove-${labelN}`}
            >
              <X className="h-3.5 w-3.5 text-red-500" />
            </button>
          )}
        </div>
      ) : (
        canEdit && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onPick}
            disabled={uploading}
            className="text-xs"
            data-testid={`task-image-upload-${labelN}`}
          >
            {uploading ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Subiendo...</>
            ) : (
              <><ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Añadir imagen</>
            )}
          </Button>
        )
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onChangeFile}
        className="hidden"
        data-testid={`task-image-input-${labelN}`}
      />
      {previewUrl && canEdit && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onPick}
          disabled={uploading}
          className="text-xs"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Reemplazar
        </Button>
      )}
    </div>
  );
}
